import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";

const links = [
  { label: "Services", id: "services" },
  { label: "Process", id: "process" },
  { label: "Work", id: "work" },
  { label: "Pricing", id: "pricing" },
  { label: "Estimate", id: "estimate" },
  { label: "Contact", id: "contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-surface/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-home-link" className="flex flex-col items-center justify-center leading-none">
          <Logo size="text-2xl" />
          <span
            data-testid="nav-tagline"
            className="hidden sm:block mt-1 text-[10px] tracking-wide text-white/45 font-body whitespace-nowrap text-center"
          >
            AI apps, agents &amp; websites — built fast.
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => goToSection(l.id)}
              className="text-sm font-medium text-white/70 hover:text-neon-cyan transition-colors"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/blog"
            data-testid="nav-blog"
            className="text-sm font-medium text-white/70 hover:text-neon-cyan transition-colors"
          >
            Blog
          </Link>
          <button
            data-testid="nav-cta-estimate"
            onClick={() => goToSection("estimate")}
            className="neon-btn bg-neon-cyan text-black font-semibold text-sm px-5 py-2 rounded-full"
          >
            Get an Estimate
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-white"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div className="md:hidden bg-surface/95 backdrop-blur-xl border-b border-white/10 px-5 py-4 flex flex-col gap-3">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-mobile-${l.id}`}
              onClick={() => goToSection(l.id)}
              className="text-left text-base font-medium text-white/80 hover:text-neon-cyan py-1"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/blog"
            data-testid="nav-mobile-blog"
            onClick={() => setOpen(false)}
            className="text-left text-base font-medium text-white/80 hover:text-neon-cyan py-1"
          >
            Blog
          </Link>
          <button
            data-testid="nav-mobile-cta"
            onClick={() => goToSection("estimate")}
            className="neon-btn bg-neon-cyan text-black font-semibold px-5 py-2.5 rounded-full mt-2"
          >
            Get an Estimate
          </button>
        </div>
      )}
    </header>
  );
}
